import { Box, Text } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useCallback, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { Button } from '../../reusable/Button'
import { LogoWithBack } from '../../reusable/LogoWithBack'
import { RichText } from '../../reusable/RichText'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { WixStudioTemplateSidePanel } from '../../reusable/WixStudioTemplate/WixStudioTemplateSidePanel'
import { Content } from './WixVibeBulletSplit.content'
import { dataHooks } from './WixVibeBulletSplit.dataHooks'

export const WixVibeBulletSplit: FC = () => {
  const { content } = useContent<Content>()
  const { questionKey, options } = content.question
  const next = useNext()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)
  const [isPrimaryButtonClicked, setPrimaryButtonClicked] = useState<boolean>(false)
  const [isSecondaryButtonClicked, setSecondaryButtonClicked] = useState<boolean>(false)
  const isButtonsDisabled = isSecondaryButtonClicked || isPrimaryButtonClicked

  const [primaryOption, secondaryOption] = options

  const handlePrimaryOptionClick = useCallback(async () => {
    if (isPageLoading) {
      return
    }
    setPrimaryButtonClicked(true)

    void pageLogger.answer({ answer: primaryOption.optionKey })

    next({ [questionKey]: { values: [primaryOption.optionKey] } })
  }, [isPageLoading, next, pageLogger, primaryOption, questionKey])

  const handleSecondaryOptionClick = useCallback(async () => {
    if (isPageLoading) {
      return
    }
    setSecondaryButtonClicked(true)

    void pageLogger.answer({ answer: secondaryOption.optionKey })

    next({ [questionKey]: { values: [secondaryOption.optionKey] } })
  }, [isPageLoading, next, pageLogger, questionKey, secondaryOption])

  const handleBack = useCallback(async () => {
    await pageLogger.back()
    window.history.back()
  }, [pageLogger])

  return (
    <WixStudioTemplate
      logo={<LogoWithBack onBackClick={handleBack} backButton={content.backButton} />}
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      backButton={content.backButton}
      sidePanel={<WixStudioTemplateSidePanel content={[content.sidePanel]} />}
      title={
        <Box maxWidth="570px" marginBottom="SP4">
          {content.title}
        </Box>
      }
    >
      <Box marginBottom="SP6" direction="vertical" gap="30px">
        {content.bullets.items.map((item, index) => (
          <Box key={index} direction="vertical" gap="6px" maxWidth="600px">
            {item.title && <Text weight="bold">{item.title}</Text>}

            <RichText data-hook={dataHooks.bullets({ id: index })} maxWidth="600px">
              {item.text}
            </RichText>
          </Box>
        ))}
      </Box>

      <Box gap="SP2" verticalAlign="middle">
        <Button
          label={primaryOption.label}
          skin="dark"
          isLoading={isPrimaryButtonClicked}
          onClick={handlePrimaryOptionClick}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          dataHook={dataHooks.mainButton()}
          disabled={isButtonsDisabled}
        />

        <Button
          label={secondaryOption.label}
          skin="dark"
          priority="secondary"
          isLoading={isSecondaryButtonClicked}
          onClick={handleSecondaryOptionClick}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          dataHook={dataHooks.secondaryButton()}
          disabled={isButtonsDisabled}
        />
      </Box>
    </WixStudioTemplate>
  )
}
